import { ReactElement, useState, useEffect, useCallback, useRef } from 'react';

import { FormHandles } from '@unform/core';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Button, ConfirmModal, GoBackButton, InputRadio } from '~/components';
import { useLoading } from '~/hooks/loading';
import { IExercise } from '~/models/Exercise';
import { ExerciseService } from '~/services';

import * as S from './styles';

interface IExerciseDetailsParams {
  exerciseId: string;
}

function ExerciseDetails(): ReactElement {
  const { setLoading } = useLoading();

  const [exercise, setExercise] = useState<IExercise>();
  const [confirmDeleteModal, setConfirmDeleteModal] = useState(false);
  const router = useHistory();
  const { exerciseId } = useParams<IExerciseDetailsParams>();
  const formRef = useRef<FormHandles>(null);

  const handleGetExercise = useCallback(async () => {
    setLoading(true);

    const response = await ExerciseService.getOneExercise(exerciseId);

    setExercise(response.data);

    setLoading(false);
  }, [exerciseId]);

  const handleOpenDeleteModal = useCallback(() => {
    setConfirmDeleteModal(true);
  }, []);

  const handleNavigateToEditExercise = useCallback(() => {
    router.push(`/ilab/exercises/edit/${exerciseId}`);
  }, [exerciseId, router]);

  const handleDeleteExercise = useCallback(() => {
    toast.success('Exercicio excluído.');
    router.goBack();
  }, [router]);

  const handleChangeExerciseEnableSubmit = useCallback(() => {
    const currentValue = formRef.current?.getFieldValue('enable');

    if (currentValue === 'Sim') {
      toast.success('Exercicio habilitado.');
    } else {
      toast.success('Exercício desabilitado.');
    }
  }, []);

  useEffect(() => {
    handleGetExercise();
  }, [handleGetExercise]);

  useEffect(() => {
    formRef.current?.setFieldValue('enable', exercise?.enable ? 'Sim' : 'Não');
  }, [exercise]);

  return (
    <>
      <GoBackButton
        containerStyle={{ alignSelf: 'flex-start', paddingTop: '2.2rem' }}
      />
      <S.Container>
        <S.Content>
          <S.Header>
            <S.Title>Detalhes do Exercício</S.Title>
          </S.Header>
          <S.InfosContainer>
            <S.InfosItem>
              <S.InfosItemTitle>Nome</S.InfosItemTitle>
              <S.InfosItemValue>{exercise?.name}</S.InfosItemValue>
            </S.InfosItem>
            <S.InfosItem>
              <S.InfosItemTitle>Segmento muscular</S.InfosItemTitle>
              <S.InfosItemValue>{exercise?.area}</S.InfosItemValue>
            </S.InfosItem>
          </S.InfosContainer>
          <S.Footer>
            <Button
              variant="black"
              textContent="Editar"
              onClick={handleNavigateToEditExercise}
              size="small"
              extraStyles={{
                maxWidth: '20rem',
                maxHeight: '3.5rem',
              }}
            />
            <Button
              variant="red"
              textContent="Excluir"
              onClick={handleOpenDeleteModal}
              size="small"
              extraStyles={{
                maxWidth: '20rem',
                maxHeight: '3.5rem',
              }}
            />
          </S.Footer>
        </S.Content>
        <S.EnableExercise>
          <S.EnableExerciseTitle>Habilitar exercício?</S.EnableExerciseTitle>
          <S.EnableExerciseForm
            ref={formRef}
            onSubmit={handleChangeExerciseEnableSubmit}
          >
            <InputRadio
              name="enable"
              wrapperStyle={{ display: 'flex', gap: '1.6rem' }}
              options={[
                { id: 'Sim', label: 'Sim' },
                { id: 'Não', label: 'Não' },
              ]}
            />
          </S.EnableExerciseForm>
        </S.EnableExercise>
      </S.Container>

      <ConfirmModal
        modalIsVisible={confirmDeleteModal}
        setModalIsVisible={setConfirmDeleteModal}
        textContent="Tem certeza de que deseja excluir esse exercício?"
        title="Excluir exercício"
        handleSubmit={handleDeleteExercise}
      />
    </>
  );
}

export default ExerciseDetails;
